// useGateOnUpdate — wraps an action handler so that, if a newer build has
// been detected via useVersionState, we interrupt with a modal before the
// action runs. Use on entry points most likely to crash on a stale build
// because they trigger lazy-chunk loads (Add Widget, Settings navigation
// etc.). The user can "Reload now" to pick up the new build before doing
// the action, or "Continue anyway" to proceed on the current build at
// their own risk.
//
// Modal-based by design: tying the interruption to the exact click means
// the user understands "this prompt is about what I just clicked." A
// banner separated from the action wouldn't connect cause-and-effect as
// clearly.

import { useCallback } from "react";
import { useVersionState } from "./useVersionState";
import { useConfirm } from "@/ui-components/ConfirmDialog/ConfirmProvider";

type AnyHandler = (...args: unknown[]) => unknown;

export function useGateOnUpdate() {
  const { isUpdateAvailable } = useVersionState();
  const confirm = useConfirm();

  return useCallback(
    <H extends AnyHandler>(handler: H): ((...args: Parameters<H>) => void) => {
      return (...args: Parameters<H>) => {
        if (!isUpdateAvailable) {
          handler(...args);
          return;
        }
        void (async () => {
          const ok = await confirm({
            title: "A new version is available",
            message:
              "Reload now to load the latest build. Continuing on the current build may cause widgets or pages to fail.",
            confirmLabel: "Reload now",
            cancelLabel: "Continue anyway",
            variant: "warning",
          });
          if (ok) {
            window.location.reload();
          } else {
            handler(...args);
          }
        })();
      };
    },
    [isUpdateAvailable, confirm]
  );
}
